import React from "react";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpectedCodeBandsHint, { type CodeBandVisibility } from "./ExpectedCodeBandsHint";
import {
  computeBaseRisingBandRange,
  formatCodeBandRange,
} from "../shared/eventCodeRange";

export type CreatorContextBandBarProps = {
  baseName?: string | null;
  subName?: string | null;
  baseDigit?: number | null;
  subDigit?: number | null;
  eventCreation?: string;
  layout?: "stack" | "horizontal";
};

const EVENT_CODE_RANGE_TOOLTIP =
  "Allowed event code range for the selected base and sub function. Rising events use 0xxxx (↑); " +
  "the matching resolving pair is in the 1xxxx range (↓, rising code + 10000).";

function EventCodeRangeInfoIcon() {
  return (
    <Tooltip
      title={EVENT_CODE_RANGE_TOOLTIP}
      enterDelay={300}
      disableInteractive
      slotProps={{
        popper: {
          sx: { pointerEvents: "none" },
        },
      }}
    >
      <InfoOutlinedIcon
        fontSize="small"
        sx={{ cursor: "help", color: "action.active", verticalAlign: "middle" }}
        aria-label="About event code range"
      />
    </Tooltip>
  );
}

function bandVisibilityForMode(eventCreation: string): CodeBandVisibility {
  if (eventCreation === "rising") return "rising";
  if (eventCreation === "resolving") return "resolving";
  return "both";
}

const MONO_SX = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  fontSize: "0.75rem",
  lineHeight: 1.35,
  color: "text.secondary",
} as const;

const LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "text.primary",
  lineHeight: 1.35,
  maxWidth: 160,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "inline-block",
  verticalAlign: "middle",
} as const;

function FunctionName({ label }: { label: string }) {
  return (
    <Typography component="span" sx={LABEL_SX} title={label}>
      {label}
    </Typography>
  );
}

export default function CreatorContextBandBar({
  baseName,
  subName,
  baseDigit,
  subDigit,
  eventCreation = "none",
  layout = "stack",
}: CreatorContextBandBarProps) {
  const hasBase = baseDigit != null;
  const hasSub = hasBase && subDigit != null;

  if (!hasBase) {
    return null;
  }

  const visibility = bandVisibilityForMode(eventCreation);
  const horizontal = layout === "horizontal";
  const baseRising = computeBaseRisingBandRange(baseDigit);

  const functionNames = (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      {baseName && <FunctionName label={baseName} />}
      {hasSub && subName && (
        <>
          <Typography component="span" sx={{ color: "text.disabled", fontSize: "0.75rem", px: 0.25 }}>
            /
          </Typography>
          <FunctionName label={subName} />
        </>
      )}
    </Box>
  );

  if (horizontal) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 1,
          rowGap: 0.5,
          px: 1.5,
          py: 0.65,
          minHeight: 40,
          boxSizing: "border-box",
          borderRadius: 0.5,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          <EventCodeRangeInfoIcon />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            Event code range
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 22, alignSelf: "center" }} />
        {functionNames}
        {hasSub ? (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25, height: 22, alignSelf: "center" }} />
            <ExpectedCodeBandsHint
              baseDigit={baseDigit}
              subDigit={subDigit}
              show={visibility}
              layout="inline"
              sx={{ fontSize: "0.75rem", lineHeight: 1.35 }}
            />
          </>
        ) : (
          <Box component="span" sx={{ ...MONO_SX, whiteSpace: "nowrap" }} title="Rising (0xxxx)">
            ↑ {formatCodeBandRange(baseRising.min, baseRising.max)}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        px: 1.75,
        py: 1.25,
        borderRadius: 1.5,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
        <EventCodeRangeInfoIcon />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: "0.75rem", color: "text.secondary" }}
        >
          Event code range
        </Typography>
      </Box>

      <Box sx={{ mb: hasSub ? 0.75 : 0 }}>{functionNames}</Box>

      {hasSub ? (
        <ExpectedCodeBandsHint
          baseDigit={baseDigit}
          subDigit={subDigit}
          show={visibility}
          sx={{ fontSize: "0.8125rem", lineHeight: 1.5 }}
        />
      ) : (
        <Typography component="div" sx={{ ...MONO_SX }}>
          ↑ {formatCodeBandRange(baseRising.min, baseRising.max)}
        </Typography>
      )}
    </Box>
  );
}
